import { createContext, useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query"; // Import TanStack Query
import { getRequest } from "services/api";
import Swal from "sweetalert2";
import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";

const SupplierContext = createContext();

export const SupplierProvider = ({ children }) => {
    const [filterOptions, setFilterOptions] = useState({
        search: "",
        currentPage: 1,
    });

    const queryString = buildQueryString({
        search: filterOptions.search || "",
        page: filterOptions.currentPage,
        paginate: LIMIT_DATA_PER_PAGE,
    });

    // Fetch data menggunakan TanStack Query
    const {
        data: suppliers,
        error,
        isLoading,
        refetch, // Untuk manual refresh data
    } = useQuery({
        queryKey: ["suppliers", filterOptions],
        queryFn: async () => {
            const response = await getRequest(`/supplier${queryString}`);
            if (!response.success) {
                throw new Error(response.message || "Failed to fetch suppliers.");
            }
            return response;
        },
        keepPreviousData: true, // Agar tidak berkedip saat berpindah halaman
    });

    // Handle pencarian (Enter untuk submit)
    const handleSearch = (e) => {
        if (e.key === "Enter") {
            if (!e.target.value.trim()) {
                Swal.fire({
                    toast: true,
                    position: "top-end",
                    icon: "warning",
                    title: "Search input cannot be empty!",
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
                return;
            }
            setFilterOptions((prev) => ({
                ...prev,
                search: e.target.value,
                currentPage: 1, // Reset ke halaman pertama setelah pencarian
            }));
        }
    };

    const handlePageChange = (page) => {
        setFilterOptions((prev) => ({
            ...prev,
            currentPage: page,
        }));
    };

    const totalPages = suppliers?.pagination?.last_page || 1;

    const Pagination = usePaginationWithComponent({
        totalPages,
        currentPage: filterOptions.currentPage,
        onPageChange: handlePageChange,
        visiblePages: 5,
    });

    return (
        <SupplierContext.Provider
            value={{
                suppliers: suppliers?.data || [],
                isLoading,
                error,
                filterOptions,
                handleSearch,
                refetch, // Bisa dipanggil untuk refresh data
                Pagination,
            }}
        >
            {children}
        </SupplierContext.Provider>
    );
};

export const useSupplier = () => {
    const context = useContext(SupplierContext);
    if (!context) {
        throw new Error("useSupplier must be used within SupplierProvider");
    }
    return context;
};
